import {
  DocumentVersionFieldFragment,
  RuleValidationInputValue,
} from '@client/graphql/__generated__/types';

export interface ValidationInputProcessingOptions {
  inputValues?: (RuleValidationInputValue | undefined)[] | null;
  contractDocumentUuid?: string;
  documentVersionFields?: DocumentVersionFieldFragment[];
}

export interface ProcessedValidationField {
  fieldKey: string;
  value: string;
  mappingKey: string;
  hasImage: boolean;
}

/**
 * Processes validation input values and matches them with document version fields
 */
export const processValidationInputs = (
  options: ValidationInputProcessingOptions
): ProcessedValidationField[] => {
  if (!options.inputValues) {
    return [];
  }

  return options.inputValues
    .filter(
      (inputValue): inputValue is RuleValidationInputValue =>
        inputValue !== undefined && inputValue !== null
    )
    .map((inputValue) => ({
      fieldKey: inputValue.fieldKey,
      value: inputValue.value,
      mappingKey: inputValue.fieldKey,
      hasImage:
        options.documentVersionFields?.some(
          (field) => field.mappingKey === inputValue.fieldKey
        ) ?? false,
    }));
};

/**
 * Filters document version fields that have corresponding validation results
 */
export const filterFieldsWithValidationResults = (
  fields: DocumentVersionFieldFragment[],
  validationMappingKeys: string[]
): DocumentVersionFieldFragment[] => {
  return fields.filter((field) =>
    validationMappingKeys.includes(field.mappingKey)
  );
};

/**
 * Creates a mapping of field keys to validation input values for quick lookup
 */
export const createFieldValueMap = (
  inputValues: (RuleValidationInputValue | undefined)[] | null
): Record<string, string> => {
  if (!inputValues) {
    return {};
  }

  return inputValues
    .filter(
      (inputValue): inputValue is RuleValidationInputValue =>
        inputValue !== undefined && inputValue !== null
    )
    .reduce(
      (acc, inputValue) => {
        acc[inputValue.fieldKey] = inputValue.value;

        return acc;
      },
      {} as Record<string, string>
    );
};
